import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { getDefaultQuantity, getTypeConfig } from './helpers';
import { requestSncrQuota } from './requestSncrQuota';
import { SncrService } from './sncrService';
import { SncrFeedback, SncrNumberingRequestItem, SncrPrescriptionTypeId } from './types';
import { useGovBrPopupAuth } from './useGovBrPopupAuth';

/**
 * Monta os itens da requisição a partir apenas dos tipos de receita dos
 * medicamentos da prescrição que exigem numeração SNCR e ainda não têm uma
 * reservada (`NumeroSncrRequerido && !NumeroSncrReservado`). Cada tipo entra
 * uma única vez, na quantidade padrão definida por config (10 para
 * notificações, 1000 para receitas brancas — lote fixo da ANVISA).
 */
function buildRequestItemsFromPendingTypes(pendingTypes: SncrPrescriptionTypeId[]): SncrNumberingRequestItem[] {
  return Array.from(new Set(pendingTypes)).map((type) => ({
    type,
    quantity: getDefaultQuantity(getTypeConfig(type)),
  }));
}

export interface SncrRequestShortcut {
  isAuthenticated: boolean;
  /** `false` enquanto a checagem inicial de autenticação ainda não resolveu. */
  authChecked: boolean;
  authRequired: boolean;
  feedback: SncrFeedback | null;
  requestNumbering(): void;
  confirmAuth(): void;
  dismissAuth(): void;
  dismissFeedback(): void;
  retry(): void;
}

/**
 * Orquestra o atalho "Solicitar numeração" do card de saldo esgotado: solicita
 * de uma vez apenas os tipos de receita dos medicamentos da prescrição que
 * ainda estão sem numeração SNCR reservada, cada um na quantidade padrão.
 *
 * Fluxo: se o prescritor já está autenticado no Gov.br, dispara a requisição
 * direto; caso contrário exibe o modal de autenticação obrigatória, e após o
 * login via popup faz a requisição automaticamente no retorno.
 *
 * @returns Estado e ações para o card renderizar os modais de autenticação,
 * processamento/resultado e erro.
 */
export function useSncrRequestShortcut(): SncrRequestShortcut {
  const doctorHash = usePrescriptionStore((store) => store.prescriberConfigurations?.Hash);
  const prescriptionItens = usePrescriptionStore((store) => store.prescription?.Itens);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [feedback, setFeedback] = useState<SncrFeedback | null>(null);

  useEffect(() => {
    if (!doctorHash) return;

    let active = true;
    setAuthChecked(false);
    SncrService.isDoctorAuthenticated(doctorHash)
      .then((authed) => active && setIsAuthenticated(authed))
      .catch(() => active && setIsAuthenticated(false))
      .finally(() => active && setAuthChecked(true));

    return () => {
      active = false;
    };
  }, [doctorHash]);

  const requestItems = useMemo(() => {
    const pendingTypes = (prescriptionItens ?? [])
      .filter((medicine) => medicine.NumeroSncrRequerido && !medicine.NumeroSncrReservado && medicine.TipoSncr)
      .map((medicine) => medicine.TipoSncr!.toUpperCase() as SncrPrescriptionTypeId);

    return buildRequestItemsFromPendingTypes(pendingTypes);
  }, [prescriptionItens]);

  const submit = useCallback(
    async (sessionId?: string) => {
      if (!doctorHash || requestItems.length === 0) return;

      setAuthRequired(false);
      await requestSncrQuota({
        doctorHash,
        items: requestItems,
        sessionId,
        onFeedback: setFeedback,
        onSessionChange: setIsAuthenticated,
      });
    },
    [doctorHash, requestItems]
  );

  const popup = useGovBrPopupAuth({
    onAuthenticated: submit,
    onError: (code) => setFeedback({ kind: 'error', code }),
    isAuthenticated: () => (doctorHash ? SncrService.isDoctorAuthenticated(doctorHash) : Promise.resolve(false)),
  });

  const requestNumbering = useCallback(() => {
    if (!doctorHash || requestItems.length === 0 || feedback?.kind === 'processing') return;

    if (isAuthenticated) {
      submit();
      return;
    }
    setAuthRequired(true);
  }, [doctorHash, requestItems, isAuthenticated, feedback, submit]);

  const confirmAuth = useCallback(() => {
    setAuthRequired(false);
    popup.openFromUserGesture();
  }, [popup]);

  const dismissAuth = useCallback(() => setAuthRequired(false), []);
  const dismissFeedback = useCallback(() => setFeedback(null), []);

  const retry = useCallback(() => {
    if (feedback?.kind !== 'error') return;

    const { code } = feedback;
    setFeedback(null);

    if (code === 'service_unavailable') {
      submit();
      return;
    }
    popup.openFromUserGesture();
  }, [feedback, submit, popup]);

  return {
    isAuthenticated,
    authChecked,
    authRequired,
    feedback,
    requestNumbering,
    confirmAuth,
    dismissAuth,
    dismissFeedback,
    retry,
  };
}
